// TODO: CSS-3941 delete this file once the account status backend exists.

import { environment } from '../../environments/environment';
import { AccountTypeIdEnum } from '../models/enum/shared/account-type-id.enum';
import { CustomError } from '../models/shared/custom-error';
import { MemberAccountStatusIdEnum } from '../models/transactions/enum/member-account-status-id.enum';
import { MemberAccount } from '../models/transactions/member-account';
import { MemberAccountStatus } from '../models/transactions/member-account-status';

interface MockStatusChange {
  statusId: MemberAccountStatusIdEnum;
  statusChangedDate: string;
}

/**
 * QA seed, read from sessionStorage so a screenshot can reach any state without editing this file.
 * `mockAccountStatuses` forces accounts into a status, e.g.
 * `{"1234": {"statusId": 4, "statusChangedDate": "2026-10-20"}}`.
 * `mockAccountStatusChangeFails` set to `true` makes the next suspend, lift or strike fail.
 */
const SEEDED_STATUSES_KEY = 'mockAccountStatuses';
const FAIL_NEXT_CHANGE_KEY = 'mockAccountStatusChangeFails';

const mockStatusChanges = new Map<number, MockStatusChange>();
const readAccounts = new Map<number, MemberAccount>();

export function mockChangeAccountStatus(accountId: number, statusId: MemberAccountStatusIdEnum): void {
  if (takeFailNextChangeSeed()) throw new CustomError();
  const statusChange = { statusId, statusChangedDate: new Date().toISOString() };
  changedAccountIds(accountId, statusId).forEach(id => mockStatusChanges.set(id, statusChange));
}

export function mockAttachStatusChange(account: MemberAccount): MemberAccount {
  const attached = withStatusChange(account);
  readAccounts.set(attached.id, attached);
  return attached;
}

function withStatusChange(account: MemberAccount): MemberAccount {
  const statusChange = mockStatusChanges.get(account.id) ?? seededStatusChange(account.id);
  if (!statusChange) return account;
  const status = Object.assign(new MemberAccountStatus(), {
    id: statusChange.statusId,
    name: MemberAccountStatusIdEnum[statusChange.statusId]
  });
  return Object.assign(new MemberAccount(), account, { ...statusChange, status });
}

/** Suspending or lifting one Defined Contribution account changes all of the member's DC accounts in that status. */
function changedAccountIds(accountId: number, statusId: MemberAccountStatusIdEnum): number[] {
  const account = readAccounts.get(accountId);
  const isSuspendOrLift = [MemberAccountStatusIdEnum.Suspended, MemberAccountStatusIdEnum.Active].contains(statusId);
  if (!account || account.typeId !== AccountTypeIdEnum.DefinedContribution || !isSuspendOrLift) return [accountId];
  const fromStatusId = currentStatusId(account);
  return [...readAccounts.values()]
    .filter(
      other =>
        other.memberId === account.memberId &&
        other.typeId === AccountTypeIdEnum.DefinedContribution &&
        currentStatusId(other) === fromStatusId
    )
    .map(other => other.id);
}

function currentStatusId(account: MemberAccount): MemberAccountStatusIdEnum {
  return mockStatusChanges.get(account.id)?.statusId ?? account.statusId;
}

function seededStatusChange(accountId: number): MockStatusChange | undefined {
  if (environment.production) return undefined;
  const seeds: Record<string, MockStatusChange> = JSON.parse(sessionStorage.getItem(SEEDED_STATUSES_KEY) ?? '{}');
  return seeds[accountId];
}

function takeFailNextChangeSeed(): boolean {
  if (environment.production || sessionStorage.getItem(FAIL_NEXT_CHANGE_KEY) !== 'true') return false;
  sessionStorage.removeItem(FAIL_NEXT_CHANGE_KEY);
  return true;
}
